import { Phone, Mail, Check, CalendarClock } from 'lucide-react';
import embelsira from "../assets/desserts/embelsira.jpeg"
import festa from "../assets/desserts/Pasticeri-Cialda-4.jpg"
import dekor from "../assets/desserts/dekor.jpg"
import { useLanguageContext } from "../context/languageContext";
import SwipeRow from './SwipeRow';
import { telHref } from '../utils/telHref';

const phoneNumber = '+355 69 205 5470';
const email = 'evente@cialda.com';

const cards = [
  { img: dekor, title: 'Elegant Atmosphere', copy: 'A sophisticated setting for your celebration' },
  { img: embelsira, title: 'Curated Desserts', copy: 'Sweet menus created specifically for your event' },
  { img: festa, title: 'Personalized Parties', copy: 'Animation and decor tailored to your event' },
];

const occasions = ['Birthdays', 'Baby Showers', 'Corporate Events', 'Anniversaries', 'Family Gatherings'];
const included = ['Custom menu planning', 'Professional service staff'];

export default function PrivateCelebrations() {
  const { t } = useLanguageContext();

  return (
    <section id="private-celebrations" className="py-12 md:py-20 bg-cream">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-block px-4 py-1.5 border border-gold/30 rounded-full mb-4">
            <span className="text-xs font-bold text-gold-deep tracking-widest uppercase">{t("Exclusive Events")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-dark-chocolate mb-3 md:mb-6">
            {t("Celebrate")} <span className="italic gradient-text-gold-deep">{t("With Us")}</span>
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-2 font-light">
            {t("Host your special occasion in our elegant atmosphere with a unique dessert experience.")}
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <SwipeRow
            gridClass="md:grid-cols-2 lg:grid-cols-3"
            gapClass="gap-3 sm:gap-6 md:gap-8"
            fadeClass="from-cream"
            className="mb-8 md:mb-16"
          >
            {cards.map(({ img, title, copy }, i) => (
              <article
                key={title}
                className={`group snap-start shrink-0 w-[80%] sm:w-[55%] md:w-auto relative h-52 sm:h-72 md:h-80 rounded-xl overflow-hidden shadow-lg transition-shadow duration-500 hover:shadow-2xl ${
                  i === 2 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <img
                  src={img}
                  alt={t(title)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 md:group-hover:scale-105"
                />
                {/* Scrim: solid behind the caption, clear across the top two
                    thirds so the photograph still reads. Positioned stops keep
                    the dark band from washing over the whole image. */}
                <div className="absolute inset-0 bg-gradient-to-t from-espresso from-0% via-espresso/85 via-35% to-transparent to-75%" />

                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-serif font-bold text-gold mb-1">
                    {t(title)}
                  </h3>
                  <p className="text-white/85 text-xs sm:text-sm leading-snug">{t(copy)}</p>
                </div>
              </article>
            ))}
          </SwipeRow>

          {/* Booking panel */}
          <div className="bg-gradient-to-br from-espresso-soft to-espresso rounded-xl shadow-xl overflow-hidden relative">
            <div className="absolute -top-16 -right-10 w-64 h-64 bg-gold/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent pointer-events-none" />

            <div className="relative z-10 grid md:grid-cols-2">
              <div className="p-5 md:p-12 flex flex-col justify-center border-b md:border-b-0 md:border-r border-white/10">
                <h3 className="text-xl md:text-3xl font-serif font-bold text-white mb-4 md:mb-6">
                  {t("Perfect for Every Occasion")}
                </h3>

                {/* Discrete chips rather than one long bullet-separated line */}
                <ul className="flex flex-wrap gap-2 mb-5 md:mb-8">
                  {occasions.map((o) => (
                    <li
                      key={o}
                      className="px-3 py-1 rounded-full border border-gold/25 bg-gold/10 text-gold text-[11px] md:text-xs font-medium"
                    >
                      {t(o)}
                    </li>
                  ))}
                </ul>

                <ul className="space-y-2.5">
                  {included.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-gold" />
                      </span>
                      <span className="text-gray-300 text-sm md:text-base">{t(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-5 md:p-12 bg-white/[0.04] flex flex-col justify-center">
                <h4 className="text-xs md:text-sm font-bold text-gold mb-4 md:mb-6 uppercase tracking-[0.2em]">
                  {t("Secure Your Date")}
                </h4>

                <div className="space-y-3 md:space-y-4 mb-5 md:mb-8">
                  <a href={telHref(phoneNumber)} className="flex items-center gap-3 group">
                    <Phone className="w-4 h-4 text-gold shrink-0" />
                    <span className="text-lg md:text-2xl text-white font-serif group-hover:text-gold transition-colors">
                      {phoneNumber}
                    </span>
                  </a>
                  <a href={`mailto:${email}`} className="flex items-center gap-3 text-gray-400 hover:text-gold transition-colors text-sm">
                    <Mail className="w-4 h-4 text-gold shrink-0" />
                    {email}
                  </a>
                  <p className="flex items-center gap-3 text-gray-400 text-sm">
                    <CalendarClock className="w-4 h-4 text-gold shrink-0" />
                    <span>
                      <span className="text-white">{t("Minimum 2 weeks in advance")}</span>
                      <span className="block text-[11px] uppercase tracking-widest text-gray-400">
                        {t("Booking Details")}
                      </span>
                    </span>
                  </p>
                </div>

                <a
                  href={telHref(phoneNumber)}
                  className="inline-flex items-center justify-center gap-2 bg-gold text-dark-chocolate font-bold text-xs md:text-sm py-3 px-6 md:py-3.5 rounded-xl hover:bg-white transition-colors duration-300 uppercase tracking-widest"
                >
                  <Phone className="w-4 h-4" />
                  {t("Book Now")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
